import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ocSetSessionToken } from "@/lib/oc-api";
import { useOcSessionStore } from "@/store/oc-session-store";

/**
 * Landing page for admin impersonation.
 *
 * The admin panel opens `/impersonate#token=…&by=…` in a new tab. The token is
 * a real portal session minted by admin-api, so all this page does is move it
 * from the URL into the place the portal already reads sessions from, then get
 * out of the way.
 *
 * The token travels in the URL *fragment* — browsers never send that to a
 * server, so it stays out of request logs. It is still stripped from the
 * address bar immediately so it does not linger in history or get copied out
 * of the location field.
 */
export function OcImpersonatePage() {
  const navigate = useNavigate();
  const setImpersonation = useOcSessionStore((s) => s.setImpersonation);
  const clearSession = useOcSessionStore((s) => s.clearSession);
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode double-invokes effects in dev; the hash is consumed
  // once and cleared, so guard against the second pass reading an empty hash.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);
    const token = params.get("token");
    const by = params.get("by");

    // Drop the fragment before anything else can read or persist it.
    window.history.replaceState(null, "", window.location.pathname);

    if (!token) {
      setError("This impersonation link is missing its token. Start again from the admin panel.");
      return;
    }

    // Any session already in this browser belongs to a different user — replace
    // it wholesale rather than merging impersonated state onto it.
    clearSession();
    ocSetSessionToken(token);
    setImpersonation(by);

    navigate("/dashboard", { replace: true });
  }, [navigate, setImpersonation, clearSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-oc-navy-900 p-6">
      <div className="max-w-md w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center">
        {error ? (
          <>
            <h1 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Impersonation link not valid
            </h1>
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{error}</p>
          </>
        ) : (
          <>
            <h1 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Signing you in…
            </h1>
            <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
              Opening the portal as the selected user.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
